import React, { FunctionComponent, useMemo } from 'react';

import Flex from '@gojob/ui-bits/Flex';
import Icon from '@gojob/ui-bits/Icon';
import Calendar from '@gojob/ui-bits/Icons/Calendar';
import Clock from '@gojob/ui-bits/Icons/Clock';
import News from '@gojob/ui-bits/Icons/News';
import usePalette from '@gojob/ui-bits/styles/PaletteProvider/usePalette';
import Text from '@gojob/ui-bits/Text';

import useDateFormat from '../../translations/useDateFormat';
import useTranslation from '../../translations/useTranslation';

export type DateTimeProps = {
  timeMinutes?: number;
  publicationDate?: string;
  media?: string;
};

type ItemProps = {
  'data-testid'?: string;
  as?: any;
  children?: React.ReactNode;
};
const Item: FunctionComponent<ItemProps> = (props) => {
  const { as, children } = props;
  const {
    button: { primary },
  } = usePalette();

  return (
    <Flex alignItems="center" margin={[2, 2]}>
      <Icon data-testid={props['data-testid']} as={as} margin={[0, 2, 0, 0]} color={primary} size={4} />
      <Text variant="message4">{children}</Text>
    </Flex>
  );
};

const DateTime: FunctionComponent<DateTimeProps> = (props) => {
  const { timeMinutes, publicationDate = '', media } = props;
  const t = useTranslation();
  const dateFormat = useDateFormat();

  const PublicationDate = useMemo(() => {
    if (!publicationDate) {
      return null;
    }
    return (
      <Item data-testid="Calendar" as={Calendar}>
        {dateFormat(Date.parse(publicationDate), 'dd MMM yyyy')}
      </Item>
    );
  }, [publicationDate, dateFormat]);

  const Media = useMemo(() => {
    if (!media) {
      return null;
    }
    return (
      <Item data-testid="News" as={News}>
        {media}
      </Item>
    );
  }, [media]);

  const Time = useMemo(() => {
    if (!timeMinutes) {
      return null;
    }
    return (
      <Item data-testid="Clock" as={Clock}>
        {t('{count}_reading_minutes', { count: timeMinutes })}
      </Item>
    );
  }, [timeMinutes, t]);

  if (!timeMinutes && !publicationDate && !media) {
    return null;
  }
  return (
    <Flex margin={[0, -2]} flexDirection="row" flexWrap="wrap">
      {Time}
      {PublicationDate}
      {Media}
    </Flex>
  );
};

export default DateTime;
